import { useQuery } from '@tanstack/react-query';
import { locale, useAppContext } from '../../contexts/AppContext';
import { ChangeEvent, useEffect, useState, useCallback } from 'react'; // Add useCallback
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import WidgetsIcon from '@mui/icons-material/Widgets';
import { Server } from '../../services/Server';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Checkbox,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import CheckIcon from '@mui/icons-material/Check';
import CompareCleaningSchedules from './CompareCleaningSchedules';
import CompareProductionLosses from './CompareProductionLosses';
import { handleError } from '../../utils/Error';
import toast from 'react-hot-toast';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PieChart from '../common/PieChart';
import SoilingRatioChart from './SoilingRatioChart';
import ProductionChart from './ProductionChart';
import HeatMapChart from './HeatMapChart';
import SMTCard from '../common/SMTCard';
import { InfoAlert } from '../common/InfoAlert';
import { getSolutionDisplayName, getYearlyDaysFromDate, isEmpty } from '../../utils/utils';
import { Solution, SolutionPackage } from '../../models/Models';
import FinancialComparison from './FinancialComparison';
import { useFormContext } from '../../contexts/FormContext';
import { getImageKeyForPackage } from '../../utils/utils';

type Props = {
  type?: string;
  setResults: any;
  project_id: string;
};

const AppSoilingOperationalStrategy = ({ type = '', setResults, project_id }: Props) => {
  const { formData } = useFormContext();
  const { setModified, role } = useAppContext(); // Add role from context
  const {
    postOperationalStrategyAPI,
    postOperationalStrategyExPostAPI,
    getCleaningPackagesAPI,
    registerProgressCallback,
    unregisterProgressCallback,
  } = Server();
  const [selectedSolutions, setSelectedSolutions] = useState<string[]>([]);
  const [chosenSolution, setChosenSolution] = useState(formData?.results?.solution);
  const [numberOfCleanings, setNumberOfCleanings] = useState(0);
  const [numberOfDevices, setNumberOfDevices] = useState(0);
  const [selectedSolution, setSelectedSolution] = useState<Solution | null>(null);
  const [copiedText, setCopiedText] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [validationMessage, setValidationMessage] = useState<string | null>(null);

  // Check if we have valid sensor data
  const [hasValidSensorData, setHasValidSensorData] = useState(false);

  // Progress state for ex-post analysis
  const [progressMessage, setProgressMessage] = useState<string>('');

  // Helper function to get the correct soiling ratio operational key based on role
  const getSoilingRatioOperationalKey = useCallback(() => {
    return role === 'ex_post_analysis' ? 'soiling_ratio_operational_ex_post' : 'soiling_ratio_operational';
  }, [role]);

  // Register progress callback for ex-post analysis
  useEffect(() => {
    if (type === 'ex-post') {
      registerProgressCallback('ex-post', (progressMessage: string) => {
        setProgressMessage(progressMessage);
      });

      return () => {
        unregisterProgressCallback('ex-post');
      };
    } else {
      registerProgressCallback('operational-strategy', (progressMessage: string) => {
        setProgressMessage(progressMessage);
      });

      return () => {
        unregisterProgressCallback('operational-strategy');
      };
    }
  }, [type, registerProgressCallback, unregisterProgressCallback]);

  useEffect(() => {
    const soilingRatioKey = getSoilingRatioOperationalKey();

    // Check if we have operational soiling ratio data
    const hasOperationalData =
      formData?.[soilingRatioKey]?.combined_pattern &&
      formData[soilingRatioKey].combined_pattern.times &&
      formData[soilingRatioKey].combined_pattern.values;

    if (!hasOperationalData) {
      setValidationMessage('Please combine soiling ratio curves first to generate the operational strategy.');
      setHasValidSensorData(false);
      return;
    }

    setValidationMessage(null);
    setHasValidSensorData(true);
  }, [formData, getSoilingRatioOperationalKey]);

  useEffect(() => {
    if (selectedSolutions.length === 1) {
      setNumberOfCleanings(data?.package_solution_groups?.[selectedSolutions?.[0]]?.optimal_cleanings);
      setNumberOfDevices(data?.package_solution_groups?.[selectedSolutions?.[0]]?.optimal_devices);
    }
  }, [selectedSolutions]);

  useEffect(() => {
    if (selectedSolutions.length === 1) {
      setSelectedSolution(
        data?.package_solution_groups?.[selectedSolutions?.[0]]?.solutions?.filter(
          (solution: Solution) => solution.num_cleanings === numberOfCleanings && solution.num_devices === numberOfDevices,
        )?.[0] || {},
      );
    }
  }, [selectedSolutions, numberOfCleanings, numberOfDevices]);

  const { data: cleaningPackages = {}, isLoading: isLoadingCleaningPackages } = useQuery({
    queryKey: ['cleaning_packages'],
    queryFn: () => getCleaningPackagesAPI(),
  });

  // Create a stable query key by filtering out fields that don't affect the operational strategy calculation
  // This prevents unnecessary refetches when metadata fields (like IDs, timestamps, results) change during save operations
  const getOperationalStrategyQueryKey = useCallback(() => {
    if (!formData) return ['operational-strategy', project_id, type];

    // Fields to exclude from the query key (metadata, internal state, results, etc.)
    const excludeFields = ['last_updated'];

    // Create a filtered copy of formData without the excluded fields
    const filteredFormData = Object.keys(formData).reduce((acc, key) => {
      if (!excludeFields.includes(key)) {
        acc[key] = formData[key];
      }
      return acc;
    }, {} as any);

    return ['operational-strategy', project_id, type, JSON.stringify(filteredFormData)];
  }, [formData, project_id, type]);

  const {
    data: data = {},
    isLoading: isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: getOperationalStrategyQueryKey(),
    queryFn: () => {
      const soilingRatioKey = getSoilingRatioOperationalKey();

      // Reset progress when starting new query
      setProgressMessage('');

      // Select the appropriate API function based on the `type` prop
      const apiFunction = type === 'ex-post' ? postOperationalStrategyExPostAPI : postOperationalStrategyAPI;

      return apiFunction(
        {
          ...formData?.location,
          cleaning_efficiency: formData?.cleaning_efficiency,
          compute_rain_efficiency: formData?.rain?.compute_rain_efficiency ?? false,
          variable_tilt_angle: formData?.variable_tilt_angle,
          ...formData?.tilt_angle,
          surface_tilt: formData?.surface_tilt,
          rain_efficiency: formData?.rain?.rain_efficiency,
          rain_threshold: formData?.rain_threshold,
          project_id: project_id,
        },
        {
          user_patterns: {
            ...(formData?.rain2.from_api !== true && formData?.rain2?.rain_pattern ? { rain_pattern: formData.rain2.rain_pattern } : {}),
            ...(formData?.relative_humidity2?.from_api !== true && formData?.relative_humidity2?.pattern
              ? { relative_humidity_pattern: formData.relative_humidity2.pattern }
              : {}),
            ...(formData?.wind_speed2?.from_api !== true && formData?.wind_speed2?.pattern
              ? { wind_speed_pattern: formData.wind_speed2.pattern }
              : {}),
          },
          // Include HSU selection and sensor files for combined soiling ratio using dynamic key
          hsu_selected: formData?.[soilingRatioKey]?.hsu_selected || false,
          sensor_files: formData?.[soilingRatioKey]?.sensor_files || [],
          use_plant_geometry: formData?.use_plant_geometry,
          plant: formData?.plant,
          workers: formData?.workers,
          financial: formData?.financial,
          yield_pattern: (() => {
            const yieldSource = formData?.yield_evaluation?.source;
            if (yieldSource === 'api') {
              return null;
            } else if (yieldSource === 'csv') {
              const pattern = formData?.yield_evaluation?.pattern;
              return pattern && typeof pattern !== 'string' ? pattern : null;
            } else if (yieldSource === 'monthly') {
              const pattern: { dates: string[]; values: number[] } = {
                dates: Array.from({ length: 12 }, (_, i) => `1900-${String(i + 1).padStart(2, '0')}-01`),
                values: formData?.yield_evaluation?.monthly_values || Array(12).fill(0),
              };
              return pattern;
            }
            return formData?.yield_evaluation?.pattern;
          })(),
          cleaning_packages:
            formData?.cleaning_package && Object.keys(formData.cleaning_package).length
              ? Object.keys(formData.cleaning_package)
                  .filter((item) => formData.cleaning_package[item] === true)
                  .map((item) => formData.cleaning_packages?.[item])
                  .filter(Boolean)
              : [],
          current_cleaning_schedule: {
            efficiency: formData?.current_efficiency,
            cleaning_opex: formData.current_cleaning_opex,
            days_to_clean: formData?.current_days_to_clean,
            cleaning_dates: getYearlyDaysFromDate(formData?.current_cleaning_dates),
          },
          blacklist_dates: getYearlyDaysFromDate(formData?.blacklist_dates),
          special_cleaning_schedule: {
            efficiency: formData?.special_efficiency_operational,
            days_to_clean: formData?.special_days_to_clean_operational,
            cleaning_dates: getYearlyDaysFromDate(formData?.special_cleaning_dates_operational),
          },
        },
      );
    },
    enabled: hasValidSensorData,
    refetchOnMount: false,
    retry: false,
  });

  if (isError) {
    //@ts-ignore
    if (Array.isArray(error?.response?.data?.detail)) {
      //@ts-ignore
      error.response?.data?.detail?.forEach((elem: { msg: string; loc: string[] }) => toast.error(`${elem.msg}: ${elem.loc.join(' - ')}`));
    } else {
      handleError(error);
    }
  }

  const calculateMonthlyAverages = (solutionData: any) => {
    if (!solutionData?.monthly?.times || !solutionData?.monthly?.values?.soiling_ratio) {
      return [];
    }

    // Initialize arrays for each month (0-11)
    const monthlyData = Array(12)
      .fill(null)
      .map(() => ({ values: [] as number[], count: 0 }));

    // Group data by month
    solutionData.monthly.times.forEach((time: any, index: number) => {
      const date = new Date(Date.parse(time));
      const month = date.getMonth(); // 0-11
      const soilingRatio = solutionData.monthly.values.soiling_ratio[index];

      if (soilingRatio !== undefined && soilingRatio !== null) {
        monthlyData[month].values.push(soilingRatio);
        monthlyData[month].count++;
      }
    });

    // Calculate averages for each month
    return monthlyData.map((monthData, monthIndex) => {
      const monthName = new Date(2024, monthIndex, 1).toLocaleString(locale, { month: 'long' });

      if (monthData.count === 0) {
        return {
          month: monthName,
          soilingRatio: 0,
          soilingLevel: 0,
          hasData: false,
        };
      }

      const averageSoilingRatio = monthData.values.reduce((sum, val) => sum + val, 0) / monthData.count;
      const averageSoilingLevel = 1 - averageSoilingRatio;

      return {
        month: monthName,
        soilingRatio: averageSoilingRatio,
        soilingLevel: averageSoilingLevel,
        hasData: true,
      };
    });
  };

  const handleTextCopy = (monthlyAverages: any[]) => {
    copyText(monthlyAverages);
    setCopiedText(true);
    setTimeout(() => {
      setCopiedText(false);
    }, 1500);
  };

  const copyText = (monthlyAverages: any[]) => {
    let txt = 'Month\tSoiling Ratio\tSoiling Level\n';
    monthlyAverages.forEach((monthData) => {
      if (monthData.hasData) {
        txt +=
          monthData.month +
          '\t' +
          Math.round(monthData.soilingRatio * 10000) / 10000 +
          '\t' +
          Math.round(monthData.soilingLevel * 10000) / 10000 +
          '\n';
      }
    });

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(txt);
    } else {
      console.error('Clipboard API is not supported in this environment.');
    }
  };

  const handleFinancialTextCopy = (text: string[]) => {
    copyFinancialText(text);
    setCopiedText(true);
    setTimeout(() => {
      setCopiedText(false);
    }, 1500);
  };
  const copyFinancialText = (text: string[]) => {
    let txt = 'Year\tCleaning Gains (MU)\tCAPEX (MU)\tCAPEX (MU)\n';
    text.forEach((t: any, index: number) => {
      txt +=
        index +
        '\t' +
        Math.round((selectedSolution?.cashflow?.cleaning_gains[index] ?? 0) * 100) / 100 +
        '\t' +
        Math.round((selectedSolution?.cashflow?.capex[index] ?? 0) * 100) / 100 +
        '\t' +
        Math.round((selectedSolution?.cashflow?.opex[index] ?? 0) * 100) / 100 +
        '\n';
    });
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(txt);
    } else {
      console.error('Clipboard API is not supported in this environment.');
    }
  };

  const handleCleaningsChange = (e: any) => {
    setNumberOfCleanings(Number(e.target.value));
  };

  const handleDevicesChange = (e: any) => {
    setNumberOfDevices(Number(e.target.value));
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    let { name, value, type, checked } = e.target;
    let val: string | number | boolean = value;
    if (type === 'number') {
      val = Number(value);
    }
    if (type === 'checkbox') {
      val = checked;
    }
    let newSelectedSolutions = [];
    if (val) {
      newSelectedSolutions = [...selectedSolutions, name];
    } else {
      newSelectedSolutions = [...selectedSolutions.filter((k) => k !== name)];
    }
    setSelectedSolutions(newSelectedSolutions);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const getOriginalKeyForImage = (packageName: string) => {
    return getImageKeyForPackage(packageName);
  };

  return (
    <>
      <Box sx={{ /*display: 'flex',*/ justifyContent: 'center', pl: 1, pr: 1 }}>
        <Typography variant="h4" color="text.primary" sx={{ mb: 2 }}>
          {type === 'ex-post' ? 'Ex-post Analysis' : 'Operational Strategy'}
        </Typography>
        <InfoAlert severity="info">
          This page provides a comparative evaluation of selected cleaning packages, highlighting the optimal schedule and best-performing
          one. It includes cost breakdown and net present value (NPV) analysis.
        </InfoAlert>

        {/* Validation message */}
        {validationMessage ? (
          <Box sx={{ mt: 2 }}>
            <InfoAlert severity="error">{validationMessage}</InfoAlert>
          </Box>
        ) : null}

        {/* Progress display for ex-post analysis */}
        {isLoading ? (
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              {progressMessage}
            </Typography>
          </Box>
        ) : null}

        {isLoading ? (
          <>
            <Skeleton width={'220px'} height={48} animation="wave" style={{ marginTop: '20px', marginBottom: '30px' }} />
            <Grid container spacing={2}>
              {Array.from(
                new Array(
                  Object.keys(formData?.cleaning_package || {}).filter((item) => (formData?.cleaning_package || {})[item] === true).length,
                ),
              ).map((item, index) => (
                <Grid
                  key={index}
                  size={{
                    xs: 12,
                    md: Math.round(
                      12 /
                        Object.keys(formData?.cleaning_package || {}).filter((item) => (formData?.cleaning_package || {})[item] === true)
                          .length,
                    ),
                  }}
                >
                  <Skeleton variant="rectangular" height={230} width={'100%'} animation="wave" />
                </Grid>
              ))}
            </Grid>

            <Skeleton width={'150px'} height={24} animation="wave" style={{ marginTop: '10px' }} />
            <Skeleton variant="rectangular" height={350} animation="wave" style={{ marginTop: '10px' }} />
            <Skeleton variant="rectangular" height={350} animation="wave" style={{ marginTop: '20px' }} />
          </>
        ) : Object.keys(data).length > 0 ? (
          <>
            {/* <pre>
              {JSON.stringify(data?.solutions, null, 4)}
            </pre> */}
            <Typography variant="h5" color="text.primary" sx={{ mt: 3 }}>
              Overview of solutions
            </Typography>
            <Grid sx={{ mt: 2, mb: 2 }} container spacing={2} key={'cl_choice'}>
              {data?.package_solution_groups &&
                Object.keys(data?.package_solution_groups)
                  .sort()
                  .map((key) => {
                    return (
                      <Grid
                        key={key}
                        size={{
                          xs: 12,
                          md:
                            Math.round(12 / Object.keys(data?.package_solution_groups).length) > 2
                              ? Math.round(12 / Object.keys(data?.package_solution_groups).length)
                              : 2,
                        }}
                      >
                        <Card>
                          <CardMedia sx={{ height: 100 }} image={'/' + getOriginalKeyForImage(key) + '.png'} title={key} />
                          <CardContent>
                            <Typography gutterBottom variant="h5" component="div">
                              {getSolutionDisplayName(cleaningPackages, key)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" component="div">
                              <Chip
                                className={'solution-chip'}
                                icon={<CleaningServicesIcon />}
                                label={'Cleanings: ' + data.package_solution_groups[key].optimal_cleanings}
                              />{' '}
                              <Chip
                                className={'solution-chip'}
                                icon={<WidgetsIcon />}
                                label={'Devices: ' + data.package_solution_groups[key].optimal_devices}
                              />{' '}
                              <Chip
                                className={'solution-chip'}
                                icon={<AttachMoneyIcon />}
                                label={
                                  'ΔNPV: ' +
                                  Math.round(
                                    data.package_solution_groups[key].solutions?.[data.package_solution_groups[key].optimal_index]
                                      ?.net_present_value / 10000,
                                  ) /
                                    100 +
                                  ' M$'
                                }
                              />
                            </Typography>
                          </CardContent>
                          <CardActions>
                            <Checkbox
                              name={key}
                              // checked={item.checked}
                              checked={selectedSolutions.includes(key) || false}
                              inputProps={{ 'aria-label': 'controlled' }}
                              onChange={(e) => handleChange(e)}
                            />
                            <Button
                              variant={chosenSolution === key ? 'contained' : 'outlined'}
                              color={chosenSolution === key ? 'success' : 'info'}
                              startIcon={<CheckIcon />}
                              onClick={() => {
                                setChosenSolution(key);
                                setResults((prevResults: any) => {
                                  return {
                                    ...prevResults,
                                    solution: key,
                                    cleaning_frequency: data?.package_solution_groups[key].optimal_cleanings,
                                    yearly_soiling_ratio:
                                      Math.round(
                                        data?.package_solution_groups[key].solutions[data?.package_solution_groups[key].optimal_index]?.data
                                          .yearly.values.soiling_ratio[0] * 10000,
                                      ) / 100,
                                    average_soiling_loss:
                                      Math.round(
                                        (data?.package_solution_groups[key].solutions[data?.package_solution_groups[key].optimal_index]
                                          ?.data.yearly?.values?.production_losses[0] /
                                          12) *
                                          100,
                                      ) / 100,
                                    needed_devices: data?.package_solution_groups[key].optimal_devices,
                                    average_cleaning_gains:
                                      Math.round(
                                        (data?.package_solution_groups[key].solutions[data?.package_solution_groups[key].optimal_index]
                                          ?.data.yearly?.values?.cleaning_gains[0] /
                                          12) *
                                          100,
                                      ) / 100,
                                  };
                                });
                                setModified(true);
                              }}
                            >
                              Choose this solution
                            </Button>
                          </CardActions>
                        </Card>
                      </Grid>
                    );
                  })}
            </Grid>
            {/* <pre>
              {JSON.stringify(solutions, null, 4)}
            </pre> */}
            {/* No solutions selected */}
            {selectedSolutions?.length === 0 ? <InfoAlert severity="error">Select a solution above</InfoAlert> : null}

            {/* 1 solution selected */}
            {selectedSolutions?.length === 1 ? (
              <>
                <Typography color="text.primary">1 solution selected.</Typography>
                <br />
                <Stack spacing={2}>
                  <Typography color="text.primary" variant="h4" sx={{ mb: 2 }}>
                    {getSolutionDisplayName(cleaningPackages, selectedSolutions?.[0])}
                  </Typography>
                  <HeatMapChart
                    name={'NPV Gain Heatmap for ' + getSolutionDisplayName(cleaningPackages, selectedSolutions?.[0])}
                    data={{ ...data.package_solution_groups[selectedSolutions[0]] }} // if you don't explode this object it will not retrigger the chart.
                  />
                  <Typography color="text.primary" variant="h5" sx={{ mb: 2 }}>
                    You can change the number of cleanings and devices below
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item size={4}>
                      <Select
                        fullWidth
                        name="nbr_of_cleanings"
                        value={numberOfCleanings}
                        onChange={(e) => handleCleaningsChange(e)}
                        sx={{ mb: 1 }}
                      >
                        {Array.from(
                          new Set(
                            data.package_solution_groups[selectedSolutions[0]]?.solutions.map((solution: any) => solution.num_cleanings),
                          ),
                        )
                          .sort((a, b) => a - b)
                          .map((val) => (
                            <MenuItem key={'c' + val} value={val as string}>
                              {val + ' cleaning' + (val !== 1 ? 's' : '')}
                            </MenuItem>
                          ))}
                      </Select>
                    </Grid>
                    <Grid item size={4}>
                      <Select fullWidth name="nbr_of_devices" value={numberOfDevices} onChange={(e) => handleDevicesChange(e)}>
                        {Array.from(
                          new Set(
                            data.package_solution_groups[selectedSolutions[0]]?.solutions.map((solution: any) => solution.num_devices),
                          ),
                        )
                          .sort((a, b) => a - b)
                          .map((val) => (
                            <MenuItem key={'c' + val} value={val as string}>
                              {val + ' device' + (val !== 1 ? 's' : '')}
                            </MenuItem>
                          ))}
                      </Select>
                    </Grid>
                    <Grid item size={4}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          height: '100%',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography variant="h6" color="text.primary">
                          NPV Gain:{' '}
                          {selectedSolution?.net_present_value
                            ? Math.round(selectedSolution.net_present_value / 10000) / 100 + ' M$'
                            : 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  {!isEmpty(selectedSolution as object) ? (
                    <>
                      <Box>
                        <Typography color="text.primary" variant="h5" sx={{ mt: 2, mb: 2 }}>
                          Plant Cleaning Time
                        </Typography>
                        <PieChart data={selectedSolution?.cleaning_time} title={''} />
                        <Typography color={'text.primary'} sx={{ textAlign: 'center' }}>
                          Total: {Math.round(selectedSolution?.time_to_clean * 100) / 100} days
                        </Typography>
                      </Box>
                      <Box>
                        <Typography color="text.primary" variant="h5" sx={{ mt: 2, mb: 2 }}>
                          Production
                        </Typography>
                        <Grid container size={12} sx={{ mb: 2 }} spacing={2}>
                          <SMTCard
                            size={4}
                            name={type === 'ex-post' ? 'Soiling Loss' : 'Average Yearly Soiling Loss'}
                            value={
                              Math.round(
                                (Array.isArray(selectedSolution?.data?.yearly?.values?.production_losses)
                                  ? type === 'ex-post'
                                    ? selectedSolution?.data?.yearly?.values?.production_losses.reduce((sum, val) => sum + val, 0)
                                    : selectedSolution?.data?.yearly?.values?.production_losses.reduce((sum, val) => sum + val, 0) /
                                      selectedSolution?.data?.yearly?.values?.production_losses.length
                                  : selectedSolution?.data?.yearly?.values?.production_losses || 0) * 100,
                              ) / 100
                            }
                            unit={'MWh'}
                            help={type === 'ex-post' ? 'yearlySoilingLoss' : 'averageYearlySoilingLoss'}
                          />
                          <SMTCard
                            size={4}
                            name={type === 'ex-post' ? 'Cleaning Gains' : 'Average Yearly Cleaning Gains'}
                            value={
                              Math.round(
                                (Array.isArray(selectedSolution?.data?.yearly?.values?.cleaning_gains)
                                  ? type === 'ex-post'
                                    ? selectedSolution?.data?.yearly?.values?.cleaning_gains.reduce((sum, val) => sum + val, 0)
                                    : selectedSolution?.data?.yearly?.values?.cleaning_gains.reduce((sum, val) => sum + val, 0) /
                                      selectedSolution?.data?.yearly?.values?.cleaning_gains.length
                                  : selectedSolution?.data?.yearly?.values?.cleaning_gains || 0) * 100,
                              ) / 100
                            }
                            unit={'MWh'}
                            help={type === 'ex-post' ? 'yearlyCleaningGains' : 'averageYearlyCleaningGains'}
                          />
                        </Grid>
                        <ProductionChart
                          reference={data?.data?.daily}
                          solutions={[
                            {
                              name: selectedSolutions?.[0],
                              ...selectedSolution,
                            },
                          ]}
                        />
                      </Box>
                      <Box sx={{ width: '100%' }}>
                        <Typography color="text.primary" variant="h5" sx={{ mt: 2, mb: 2 }}>
                          Financial
                          <Tooltip title={copiedText ? 'Table copied!' : 'Copy table'}>
                            <IconButton
                              onClick={() => {
                                handleFinancialTextCopy(selectedSolution?.cashflow?.cleaning_gains?.map((item, index) => index));
                              }}
                            >
                              {copiedText ? (
                                <CheckCircleIcon fontSize="small" sx={{ color: '#008000' }} />
                              ) : (
                                <ContentCopyRoundedIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Typography>
                        <Box sx={{ flexGrow: 1 }}>
                          <Grid container spacing={2}>
                            <Grid size={type === 'ex-post' ? 12 : 6}>
                              <TableContainer sx={{ maxHeight: 840 }} component={Paper}>
                                <Table stickyHeader size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Year</TableCell>
                                      <TableCell align="right">Cleaning Gains ($)</TableCell>
                                      <TableCell align="right">CAPEX ($)</TableCell>
                                      <TableCell align="right">OPEX ($)</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {selectedSolution?.cashflow?.cleaning_gains
                                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                      .map((item, index) => {
                                        const idx = page * rowsPerPage + index;
                                        return (
                                          <TableRow key={idx}>
                                            <TableCell>Year {selectedSolution?.cashflow?.years[idx]}</TableCell>
                                            <TableCell align="right">{Math.round(item * 100) / 100}</TableCell>
                                            <TableCell align="right">
                                              {Math.round(selectedSolution?.cashflow?.capex[idx] * 100) / 100}
                                            </TableCell>
                                            <TableCell align="right">
                                              {Math.round(selectedSolution?.cashflow?.opex[idx] * 100) / 100}
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                              <TablePagination
                                rowsPerPageOptions={[10, 30, 50, 100]}
                                component="div"
                                count={selectedSolution?.cashflow?.cleaning_gains?.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                              />
                            </Grid>
                            {type !== 'ex-post' ? (
                              <Grid size={6}>
                                <Grid container spacing={2}>
                                  <Grid size={12} justifyContent="center" alignItems="center">
                                    <InfoAlert severity="info">
                                      The pie charts below show the distribution of total CAPEX and OPEX across the entire optimization
                                      horizon, actualized to today's value using the discount rate.
                                    </InfoAlert>
                                  </Grid>
                                  <Grid size={12} justifyContent="center" alignItems="center">
                                    <PieChart
                                      data={selectedSolution?.capex}
                                      title={'Cleaning CAPEX ($)'}
                                      unit={'$'}
                                      // width={300}
                                    />
                                    <Typography color={'text.primary'} sx={{ textAlign: 'center' }}>
                                      Total: {Math.round(selectedSolution?.capex?.total * 100) / 100} $
                                    </Typography>
                                  </Grid>
                                  <Grid size={12} justifyContent="center" alignItems="center">
                                    <PieChart
                                      data={selectedSolution?.opex}
                                      title={'Cleaning OPEX ($)'}
                                      unit={'$'}
                                      // width={300}
                                    />
                                    <Typography color={'text.primary'} sx={{ textAlign: 'center' }}>
                                      Total: {Math.round(selectedSolution?.opex?.total * 100) / 100} $
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Grid>
                            ) : null}
                          </Grid>
                        </Box>
                      </Box>
                      <Box>
                        <Typography color="text.primary" variant="h5" sx={{ mt: 2, mb: 2 }}>
                          Soiling Ratio
                        </Typography>
                        <Grid container size={12} sx={{ mb: 2 }}>
                          <SMTCard
                            size={4}
                            name={'Average Yearly Soiling Level'}
                            value={
                              Math.round(
                                (1 -
                                  (Array.isArray(selectedSolution?.data?.yearly?.values?.soiling_ratio)
                                    ? selectedSolution?.data?.yearly?.values?.soiling_ratio.reduce((sum, val) => sum + val, 0) /
                                      selectedSolution?.data?.yearly?.values?.soiling_ratio.length
                                    : selectedSolution?.data?.yearly?.values?.soiling_ratio || 0)) *
                                  10000,
                              ) / 100
                            }
                            unit={'%'}
                          />
                        </Grid>

                        <SoilingRatioChart
                          rain_dates={data?.rain_dates}
                          reference={data?.data?.daily}
                          solutions={
                            selectedSolutions?.[0] && data?.package_solution_groups?.[selectedSolutions?.[0]]
                              ? [
                                  {
                                    name: selectedSolutions?.[0],
                                    ...selectedSolution,
                                  },
                                ]
                              : []
                          }
                          ymin={0.4}
                          ymax={1.2}
                        />
                      </Box>
                      <Box>
                        <Typography color="text.primary" variant="h6" sx={{ mt: 2, mb: 2 }}>
                          Monthly Average Soiling Ratio
                        </Typography>
                        <Card sx={{ mt: 2 }}>
                          <CardContent>
                            <Typography variant="h5" noWrap color="text.primary" sx={{ mt: 1 }}>
                              Monthly Average Soiling Ratio
                              <Tooltip title={copiedText ? 'Table copied!' : 'Copy table'}>
                                <IconButton
                                  onClick={() => {
                                    const monthlyAverages = calculateMonthlyAverages(selectedSolution?.data);
                                    handleTextCopy(monthlyAverages);
                                  }}
                                >
                                  {copiedText ? (
                                    <CheckCircleIcon fontSize="small" sx={{ color: '#008000' }} />
                                  ) : (
                                    <ContentCopyRoundedIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </Typography>
                            <TableContainer component={Paper} sx={{ mt: 1 }}>
                              <Table sx={{ minWidth: 650 }} size="small" aria-label="simple table">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Month</TableCell>
                                    <TableCell align="right">Soiling Ratio (%)</TableCell>
                                    <TableCell align="right">Soiling Level (%)</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {calculateMonthlyAverages(selectedSolution?.data).map((monthData, index) => {
                                    const soiling_ratio = monthData.hasData ? Math.round(monthData.soilingRatio * 10000) / 100 : 0;
                                    const soiling_level = monthData.hasData ? Math.round(monthData.soilingLevel * 10000) / 100 : 0;
                                    return (
                                      <TableRow key={monthData.month} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                        <TableCell component="th" scope="row">
                                          {monthData.month}
                                        </TableCell>
                                        <TableCell align="right">{soiling_ratio}</TableCell>
                                        <TableCell align="right">{soiling_level}</TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </CardContent>
                        </Card>
                      </Box>
                    </>
                  ) : (
                    <Typography>This combination doesn't exist. </Typography>
                  )}
                </Stack>
              </>
            ) : null}
            {/* More than 1 solution selected */}
            {selectedSolutions.length > 1 ? (
              <>
                <FinancialComparison
                  data={selectedSolutions?.map((name) => {
                    return {
                      name: name,
                      ...data?.package_solution_groups[name].solutions[data?.package_solution_groups[name].optimal_index],
                    };
                  })}
                />
                <ProductionChart
                  reference={data?.data?.daily}
                  solutions={selectedSolutions?.map((name) => {
                    return {
                      name: name,
                      ...data?.package_solution_groups[name].solutions[data?.package_solution_groups[name].optimal_index],
                    };
                  })}
                />
                <CompareProductionLosses
                  data={selectedSolutions?.map((name) => {
                    return {
                      name: name,
                      ...data?.package_solution_groups[name].solutions[data?.package_solution_groups[name].optimal_index],
                    };
                  })}
                />
                <CompareCleaningSchedules
                  rain_dates={data?.rain_dates}
                  data={selectedSolutions?.map((name) => {
                    return {
                      name: name,
                      ...data?.package_solution_groups[name].solutions[data?.package_solution_groups[name].optimal_index],
                    };
                  })}
                />
                <SoilingRatioChart
                  rain_dates={data?.rain_dates}
                  reference={data?.data?.daily}
                  solutions={selectedSolutions?.map((name) => {
                    return {
                      name: name,
                      ...data?.package_solution_groups[name].solutions[data?.package_solution_groups[name].optimal_index],
                    };
                  })}
                />
              </>
            ) : null}
          </>
        ) : null}
      </Box>
    </>
  );
};

export default AppSoilingOperationalStrategy;
